
import { getLocalizedCountValue, format } from '@ms/utilities-strings/lib/index';
import {
    ONE_MINUTE,
    TWO_MINUTES,
    ONE_HOUR,
    TWO_HOURS,
    ONE_DAY,
    TWO_DAYS,
    ONE_MONTH
} from './helpers';
import DateTimeResx from './DateTime.resx';

/**
 * Get a string like "X minutes ago" that reflects the time elapsed since the input time.
 * Only works for past times, future times just return a browser-determined localized time string.
 */
export function getRelativeDateTimeStringPast(pastTime: Date, language: string, startWithLowerCase: boolean = false): string {
    const timespan: number = Date.now() - pastTime.getTime(); // time elapsed in ms

    if (timespan < -5 * ONE_MINUTE) { // in the future, with a 5m leeway
        return pastTime.toLocaleDateString(language);
    } else if (timespan < ONE_MINUTE) { // 1m ago to 5m in the future
        // "Less than a minute ago"
        return getRelativeDateTimeResx('LessThanAMinute', startWithLowerCase);
    } else if (timespan < TWO_MINUTES) {
        // "About a minute ago"
        return getRelativeDateTimeResx('AboutAMinute', startWithLowerCase);
    } else if (timespan < ONE_HOUR) {
        // "{0} minutes ago"
        const minutes = Math.floor(timespan / ONE_MINUTE);
        return getRelativeDateTimeIntervalString(minutes, 'XMinutes');
    } else if (timespan < TWO_HOURS) {
        // "About an hour ago"
        return getRelativeDateTimeResx('AboutAnHour', startWithLowerCase);
    } else if (timespan < ONE_DAY) {
        // "{0} hours ago"
        const hours = Math.floor(timespan / ONE_HOUR);
        return getRelativeDateTimeIntervalString(hours, 'XHours');
    } else if (timespan < TWO_DAYS) {
        // "Yesterday at {0}"
        let locTime = pastTime.toLocaleTimeString(language);
        return getRelativeDateTimeResx('YesterdayAndTime', startWithLowerCase)
            .replace('{0}', locTime);
    } else if (timespan < ONE_MONTH) {
        // "{0} days ago" (in the past month-ish)
        const days = Math.floor(timespan / ONE_DAY);
        return getRelativeDateTimeIntervalString(days, 'XDays');
    }

    // Any other time, just return the regular full original time
    return pastTime.toLocaleDateString(language); // browser-determined localized date (no time)
}

/**
 * This is a modified implementation of `DateTime.GetRelativeDateTimeStringPast(...)`.
 * The differences here are as follows:
 *   1. The time string for yesterday does not include the seconds
 *   2. Instead of showing 'X days ago' for dates older than a month, default to showing the full date
 *   3. The full date will also include the time (also without seconds)
 */
export function getRelativeDateTimeStringPastWithHourMinute(pastTime: Date, language: string): string {
    let timespan: number = Date.now() - pastTime.getTime(); // time elapsed in ms
    let date: string = pastTime.toLocaleDateString(language); // browser-determined localized date
    let time: string = pastTime.toLocaleTimeString(language, { hour: 'numeric', minute: '2-digit' }); // time without seconds

    if (timespan < ONE_DAY) {
        return getRelativeDateTimeStringPast(pastTime, language);
    } else if (timespan < TWO_DAYS) {
        // "Yesterday at {0}" without seconds
        return format(DateTimeResx.RelativeDateTime_YesterdayAndTime, time);
    }

    // Any other time, just return the regular full original date with time, without seconds
    return format(DateTimeResx.DateTime_DateAndTime, date, time);
}

export type RelativeDateTimeResxName = 'AFewSeconds' | 'LessThanAMinute' | 'AboutAMinute' |
    'XMinutes' | 'XMinutesIntervals' |
    'AboutAnHour' | 'XHours' | 'XHoursIntervals' |
    'Today' | 'Yesterday' | 'YesterdayAndTime' | 'Tomorrow' | 'TomorrowAndTime' |
    'XDays' | 'XDaysIntervals';

/**
 * Internal helper function to reduce verbosity of string names.
 */
export function getRelativeDateTimeResx(
    name: RelativeDateTimeResxName,
    startWithLowerCase?: boolean,
    isFuture?: boolean,
    isInterval?: boolean
): string {
    // Example full string names:
    // RelativeDateTime_AboutAnHour
    // RelativeDateTime_AboutAnHour_StartWithLowerCase
    // RelativeDateTime_AboutAnHourFuture
    // RelativeDateTime_AboutAnHourFuture_StartWithLowerCase
    return (<any>DateTimeResx)[[ // tslint:disable-line:no-any
        'RelativeDateTime_',
        name,
        isFuture ? 'Future' : '',
        isInterval ? 'Intervals' : '', // see next method for examples of this
        startWithLowerCase ? '_StartWithLowerCase' : ''
    ].join('')];
}

/**
 * Internal helper function to get a localized count value for an interval.
 */
export function getRelativeDateTimeIntervalString(
    value: number,
    intervalType: 'XMinutes' | 'XHours' | 'XDays',
    startWithLowerCase?: boolean,
    isFuture?: boolean
): string {
    // Example full string names:
    // RelativeDateTime_XMinutes
    // RelativeDateTime_XMinutesFuture
    // RelativeDateTime_XMinutesFuture_StartWithLowerCase
    // (note non-future variant has no lowercase variant...so English-centric)
    const mainResx = getRelativeDateTimeResx(intervalType, isFuture ? startWithLowerCase : false, isFuture);
    // Example full string names:
    // RelativeDateTime_XMinutesIntervals
    // RelativeDateTime_XMinutesFutureIntervals
    const intervalResx = getRelativeDateTimeResx(intervalType, false /*lowercase*/, isFuture, true /*intervals*/);

    return (getLocalizedCountValue(mainResx, intervalResx, value) || '').replace('{0}', value + '');
}
